import { useMutation, useQueryClient } from '@tanstack/react-query';

import * as Filtrator from '@Stores/Filtrator';
import * as PageProductStore from '@Stores/PageProduct';
import Configurator from '@Api/Configurator';
import filterInStock from './helpers/filterInStock';
import useKeys from './useKeys';

import type { ConfiguratorData, ConfiguratorOption } from '@Pages/PageProduct';
import type { ParamsData } from '@Api/Configurator/getFilterResult';

const useApplyFilters = () => {
  const queryClient = useQueryClient();
  const {
    product: { id },
  } = PageProductStore.useData();
  const { configurator: keys, filters: filterKeys } = useKeys();
  const entityId = 'configurator-textile';
  const selected = Filtrator.useSelected(entityId);
  const filtrator = Filtrator.useFiltrator(entityId);

  const updateOptions = (options: ConfiguratorOption[], isBasicStock: boolean) =>
    options.map((option) => ({
      ...option,
      items: option.items.map((item) => {
        return {
          ...item,
          calcDisabled: isBasicStock ? item.disabled : false,
        };
      }),
    }));

  const { mutateAsync, isPending } = useMutation({
    mutationKey: filterKeys,
    mutationFn: async () => {
      const params: ParamsData = {};

      Object.keys(selected.parameters).forEach((parameter) => {
        if (parameter === 'price') {
          params[parameter] = {
            min: +selected.parameters[parameter].default[0],
            max: +selected.parameters[parameter].default[1],
          };
        } else {
          params[parameter] = selected.parameters[parameter].default;
        }
      });

      const res = await Configurator.getFilterResult({
        shopProductId: id,
        params,
      });

      return res;
    },
    onSuccess: (data) => {
      const inStock = +filtrator.parameters.in_stock?.default === 1;
      queryClient.setQueryData(keys, (prev: ConfiguratorData) => {
        if (!prev) return;

        const { isBasic } = prev.configuration;
        const isBasicStock = inStock && isBasic;

        const filteredTabs = prev.tabs.map((tab) => {
          const isUpholstery = tab.id === 'upholstery';

          const updatedOptions = isUpholstery
            ? filterInStock({
                tabs: [tab],
                currentInStock: true,
                filters: { ...filtrator, ...data },
                inStock,
                isBasic,
              })[0].options
            : updateOptions(tab.options, isBasicStock);

          return {
            ...tab,
            ...(isUpholstery && {
              filtersInfo: filtrator,
              count: filtrator.totalCount,
            }),
            options: updatedOptions,
          };
        });

        return { ...prev, tabs: filteredTabs };
      });

      queryClient.setQueryData(filterKeys, () => ({
        ...data,
        filters: filtrator,
      }));
    },
  });

  return {
    applyFilters: mutateAsync,
    isPending,
  };
};

export default useApplyFilters;
